import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import SwiperCore from "swiper";
import debounce from "lodash.debounce";

import { useScrollBlock } from "@hooks";

import { ItemType } from "../types";
import {
  LocalizationKeys,
  GALLERY_THUMBNAILS_CONTAINER_CLASS,
  GALLERY_ITEM_CLASS,
  SLIDE_TRANSITION_DURATION_MS,
} from "../constants";
import { getItemData } from "../utils";

type LocalizationType = Record<keyof typeof LocalizationKeys, string>;

interface Props {
  focusLockRef: MutableRefObject<HTMLDivElement>;
  swiperInstance: SwiperCore;
}

interface Output {
  isOpening: boolean;
  isOpen: boolean;
  isClosing: boolean;
  localization: LocalizationType;
  items: ItemType[];
  handleCloseButtonClick: () => void;
  handleLightboxFading: () => void;
  clickedThumbnailIndex: number;
  activeDescriptionHeight: number;
  setActiveDescriptionRef: (ref: MutableRefObject<HTMLDivElement>) => void;
  setActiveContentRef: (ref: MutableRefObject<HTMLDivElement>) => void;
  activeContentHeight: number;
}

const useLightbox = ({ focusLockRef, swiperInstance }: Props): Output => {
  const { allowScroll, blockScroll } = useScrollBlock();

  const swiperInstanceRef = useRef<SwiperCore>(swiperInstance);
  const shouldListenToKeyDownRef = useRef<boolean>(false);
  const isOpenRef = useRef<boolean>(false);
  const isClosingRef = useRef<boolean>(false);

  /* Use content ref and description ref for usage in event listener */
  const activeContentRef = useRef<HTMLDivElement>(null);
  const activeDescriptionRef = useRef<HTMLDivElement>(null);

  const [thumbnailsContainer, setThumbnailsContainer] = useState<HTMLElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [clickedThumbnailIndex, setClickedThumbnailIndex] = useState(0);
  const [shouldListenToKeyDown, setShouldListenToKeyDown] = useState(false);
  const [_activeContentRef, setActiveContentRef] = useState<MutableRefObject<HTMLDivElement>>(null);
  const [_activeDescriptionRef, setActiveDescriptionRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);
  const [activeDescriptionHeight, setActiveDescriptionHeight] = useState(0);
  const [activeContentHeight, setActiveContentHeight] = useState(0);

  /**
   * Set localization from dataset
   */
  const localization: LocalizationType = useMemo(() => {
    if (!thumbnailsContainer) return null;

    const { dataset } = thumbnailsContainer;

    return {
      CLOSE_TITLE: dataset?.[LocalizationKeys.CLOSE_TITLE],
      NEXT_TITLE: dataset?.[LocalizationKeys.NEXT_TITLE],
      PREVIOUS_TITLE: dataset?.[LocalizationKeys.PREVIOUS_TITLE],
    };
  }, [thumbnailsContainer]);

  /**
   * Listen on thumbnail click, set slideIndex state and open lightbox
   */
  const handleThumbnailClick = (event: Event, thumbnailIndex: number): void => {
    event.preventDefault();

    if (!swiperInstanceRef?.current) return;

    /**
     * Swiper is copying one slide because of infinite gallery swiping,
     * so it is adding one extra slide (and also index), so real index is always + 1
     */
    const realSwiperSlideIndex = thumbnailIndex + 1;

    swiperInstanceRef.current.slideTo(realSwiperSlideIndex, 0, false);

    setClickedThumbnailIndex(thumbnailIndex);
    setIsOpening(true);
  };

  /**
   * Set items
   */
  const items: ItemType[] = useMemo(() => {
    if (!thumbnailsContainer) return null;

    const itemElements = thumbnailsContainer.querySelectorAll(`.${GALLERY_ITEM_CLASS}`);
    const itemElementsArr = Array.from(itemElements || []);

    if (!itemElementsArr?.length) return null;

    const itemsWithData: ItemType[] = itemElementsArr.map((item, index) => {
      item.addEventListener("click", (e) => handleThumbnailClick(e, index));

      /**
       * pointer-events: none; class added to prevent click on thumbnail
       * without attached event to item
       *
       * Remove the class after listener is attached
       */
      item.classList.remove("pointer-events-none");

      return getItemData({ item: item as HTMLAnchorElement, index });
    });

    return itemsWithData;
  }, [thumbnailsContainer]);

  /**
   * Close button click listener
   */
  const handleCloseButtonClick = (): void => {
    if (!isOpenRef.current || isClosingRef.current) return;

    setIsClosing(true);
  };

  /**
   * Hide lightbox
   */
  const handleLightboxFading = (): void => {
    if (!isClosing) return;

    setIsOpen(false);
    setIsClosing(false);
  };

  /**
   * Handle keydown while lightbox is open
   */
  const handleOnKeyDown = (event: KeyboardEvent): void => {
    if (!swiperInstanceRef?.current || !shouldListenToKeyDownRef?.current) return;

    const { key, code } = event;

    /**
     * Allow spacebar, enter and tab keys default behaviour
     * to prevent keyboard navigation interruption
     *
     * check: https://www.toptal.com/developers/keycode
     */
    if (key === "Enter" || key === "Tab" || code === "Space") {
      return;
    }

    event.preventDefault();

    switch (key) {
      case "Escape": {
        handleCloseButtonClick();
        return;
      }
      case "ArrowLeft": {
        swiperInstanceRef.current.slidePrev();

        break;
      }
      case "ArrowRight": {
        swiperInstanceRef.current.slideNext();

        break;
      }
      default:
        break;
    }
  };

  /**
   * Calculate active content height for css recalculation
   */
  const calculateContentHeight = (): void => {
    const contentEl = activeContentRef?.current;

    if (!contentEl) {
      setActiveContentHeight(0);
      return;
    }

    /* Height + padding */
    const newDescriptionHeight = contentEl.clientHeight;
    setActiveContentHeight(newDescriptionHeight);
  };

  /**
   * Calculate active description height for css recalculation
   */
  const calculateDescriptionHeight = (): void => {
    const descriptionEl = activeDescriptionRef?.current;

    if (!descriptionEl) {
      setActiveDescriptionHeight(0);
      return;
    }

    /* Height + padding */
    const newDescriptionHeight = descriptionEl.clientHeight;
    setActiveDescriptionHeight(newDescriptionHeight);
  };

  /**
   * Change active description ref state on slide swap
   */
  useEffect(() => {
    if (!_activeDescriptionRef?.current) return;

    activeDescriptionRef.current = _activeDescriptionRef.current;

    calculateDescriptionHeight();
  }, [_activeDescriptionRef]);

  /**
   * Change active content element state on slide swap
   */
  useEffect(() => {
    if (!_activeContentRef?.current) return;

    activeContentRef.current = _activeContentRef.current;

    calculateContentHeight();
  }, [_activeContentRef]);

  /**
   * Set swiperInstanceRef from swiperInstance prop for usage in
   * event handler
   */
  useEffect(() => {
    if (!swiperInstance) return;

    swiperInstanceRef.current = swiperInstance;
  }, [swiperInstance]);

  /**
   * Set isOpenstate to true after isOpening is set to true.
   *
   * isOpening state will remove lightbox--closed class with display: none;
   * which will allow opacity transition on lightbox opening
   */
  useEffect(() => {
    if (!isOpening) return;

    /* Prevent flickering */
    window.setTimeout(() => {
      setIsOpen(true);
    }, 50);
  }, [isOpening]);

  /**
   * Set isOpen ref for usage value in event listener
   * anddisable/enable body scroll on lightbox open state change
   */
  useEffect(() => {
    /**
     * Set isOpenRef value based on isOpen state change
     * for usage in handleKeyDown event listener (in Esc close event)
     */
    isOpenRef.current = isOpen;

    if (isOpen) {
      blockScroll();
      setIsOpening(false);

      return;
    }

    allowScroll();
  }, [isOpen]);

  /**
   * Set isClosingRef value based on isClosing state change
   * for usage in handleKeyDown event listener (in Esc close event)
   */
  useEffect(() => {
    isClosingRef.current = isClosing;
  }, [isClosing]);

  /**
   * Prevent unecessarry keydown events
   */
  useEffect(() => {
    if (isOpen && !isOpening && !isClosing) {
      window.setTimeout(() => {
        setShouldListenToKeyDown(true);
      }, SLIDE_TRANSITION_DURATION_MS);

      return;
    }

    setShouldListenToKeyDown(false);
  }, [isOpen, isOpening, isClosing]);

  /**
   * Set shouldListenToKeyDownRef after shouldListenToKeyDown change
   * for usage in keyDown event handler
   */
  useEffect(() => {
    shouldListenToKeyDownRef.current = shouldListenToKeyDown;
  }, [shouldListenToKeyDown]);

  /**
   * Set thumbnail container to slide elements data from thumbnail elements
   * and add keyDown event listener
   */
  useEffect(() => {
    if (!focusLockRef?.current) return;

    const { current: focusLockRefEl } = focusLockRef;

    const { parentNode: lightboxParentNode } = focusLockRefEl;
    const { parentNode: thumbnailsParent } = lightboxParentNode;

    const thumbnailsContainerEl = thumbnailsParent.querySelector(
      `.${GALLERY_THUMBNAILS_CONTAINER_CLASS}`
    );

    if (!thumbnailsContainerEl) return;

    setThumbnailsContainer(thumbnailsContainerEl as HTMLElement);
  }, [focusLockRef]);

  /**
   * Add event listener for keydown
   */
  useEffect(() => {
    const resizeListener = debounce(() => {
      calculateDescriptionHeight();
      calculateContentHeight();
    }, 300);

    window.addEventListener("keydown", handleOnKeyDown);
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("keydown", handleOnKeyDown);
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  return {
    handleCloseButtonClick,
    isClosing,
    isOpen,
    isOpening,
    items,
    localization,
    handleLightboxFading,
    clickedThumbnailIndex,
    activeDescriptionHeight,
    setActiveDescriptionRef,
    setActiveContentRef,
    activeContentHeight,
  };
};

export default useLightbox;
